import {
  FieldArrayWithId,
  FieldValues,
  useFieldArray,
  useFormContext,
} from 'react-hook-form'
import { DetailForm } from './DetailForm'
import { Button } from '@/components/ui/button'
import { Plus, Trash } from 'lucide-react'
import { DetailSubFormHeader } from './DetailSubFormHeader'
import { Fragment } from 'react/jsx-runtime'
import { FormFieldData } from './fields/type'
import { DetailManyToOneProvider } from './DetailManyToOneProvider'
import { useMemo, useCallback } from 'react'

type Props<T, R> = {
  name: keyof T
  title: string
  maxLength?: number
  getFields: (index: number) => FormFieldData<R>[]
  children?: (index: number) => React.ReactNode
  addButtonText?: string
  hideDeleteButton?: boolean
}

export const DetailManyToOne = <T, R = any>({
  name,
  title,
  maxLength,
  getFields,
  children,
  addButtonText,
  hideDeleteButton,
}: Props<T, R>) => {
  const context = useFormContext<FieldValues, T>()
  const array = useFieldArray<FieldValues, T, '@id'>({
    name,
    control: context.control,
    keyName: '@id',
  })

  const getValue = (index: number) => {
    const itemName = `${name}[${index}]`
    return context.getValues(itemName)
  }

  const onDelete = useCallback(
    (index: number) => {
      if (!confirm('정말로 삭제하시겠습니까?')) {
        return
      }

      array.remove(index)
    },
    [array],
  )

  return (
    <DetailManyToOneProvider name={name} array={array}>
      <div className="space-y-1 px-5">
        <DetailSubFormHeader title={title} />
        <div className="px-2 text-sm text-gray-500">
          총 {array.fields.length}개
        </div>
        <div className="space-y-2">
          {array.fields.map((field, index) => {
            const fieldId = field['@id']
            const itemName = `${name}[${index}]`

            return (
              <Fragment key={fieldId}>
                <div className="relative rounded-lg border p-4">
                  {!hideDeleteButton && (
                    <div className="absolute right-2 top-2">
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => onDelete(index)}
                        type="button"
                        className="size-8"
                      >
                        <Trash className="size-4" />
                      </Button>
                    </div>
                  )}
                  <DetailForm
                    name={itemName}
                    data={getValue(index)}
                    fields={getFields(index)}
                  />
                  {children?.(index)}
                </div>
              </Fragment>
            )
          })}
          {(typeof maxLength === 'undefined' ||
            array.fields.length < maxLength) && (
            <Button
              onClick={() =>
                array.append(
                  getFields(0).reduce(
                    (acc, field) => {
                      if (!(field.key in acc)) {
                        acc[field.key] = field.default || null
                      }
                      return acc
                    },
                    {
                      order: array.fields.length + 1,
                    },
                  ),
                )
              }
              variant="outline"
              size="sm"
              type="button"
            >
              <Plus className="mr-1" size={16} />{' '}
              {addButtonText || `${title} 더 추가하기`}
            </Button>
          )}
        </div>
      </div>
    </DetailManyToOneProvider>
  )
}
